// ------------ import external dependencies -------------
import { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { H } from "highlight.run";

// ------------- import internal dependencies --------------
import AuthLayout from "../../../layouts/authLayout/AuthLayout";
import FormInput from "../../../components/Forms/input/FormInput";
import { loginValidator } from "../../../utils/validators/auth";
// ----- import hooks -------
import useAuth from "../hooks/useAuth";
import {
  FormWrapper,
  FormHeaderText,
  Button,
  FormSubTitle,
  TextLight,
} from "../styles/auth";
import { useStoreWrapper } from "../../../store/store";
import {
  AUTH_API_TOKEN_KEY,
  MUST_CHANGE_PASSWORD_KEY,
} from "../../../api/authApi";
import InvalidCredentialsModal from "./InvalidCredentialsModal";
import { isInvalidCredentialsError } from "./loginErrors";

const Login = () => {
  const [showInvalidLogin, setShowInvalidLogin] = useState(false);

  // ------- intialize custom hooks -------
  const { mrcLogin } = useAuth();
  const navigate = useNavigate();

  const {
    updateUser,
    updateAccessToken,
    setUserFacilities,
    setRoles,
    setMode,
    setUserIncidentStatus,
    setUserIncidentTypes,
  } = useStoreWrapper((state) => ({
    updateUser: state.updateUser,
    updateAccessToken: state.updateAccessToken,
    setUserFacilities: state.setUserFacilities,
    setRoles: state.setRoles,
    setMode: state.setMode,
    setUserIncidentStatus: state.setUserIncidentStatus,
    setUserIncidentTypes: state.setUserIncidentTypes,
  }));

  const [params] = useSearchParams();

  const { isLoading, mutateAsync: login } = mrcLogin;

  const formik = useFormik({
    initialValues: {
      emailOrUsername: "",
      password: "",
    },
    validationSchema: loginValidator(),
    onSubmit: async (values: any) => {
      const data = {
        usernameOrEmail: values.emailOrUsername,
        password: values.password,
      };
      try {
        const response = await login(data);

        const portal = response.data.portal;

        // The .NET token lives under its own key; "accessToken" must stay the Salesforce token.
        if (response.data.token?.accessToken) {
          localStorage.setItem(
            AUTH_API_TOKEN_KEY,
            response.data.token.accessToken
          );
        } else {
          // Logged, not thrown: the portal session is still valid.
          // eslint-disable-next-line no-console
          console.error(
            "Login succeeded but returned no .NET bearer token. Password changes will " +
              "fail with 401 until this is fixed."
          );
        }

        if (portal.webAuthToken) {
          localStorage.setItem("webAuthToken", portal.webAuthToken);
        }

        // Branch on .NET's mustChangePassword, not portal.user.firstTimeLoggedIn, which the
        // Firebase change-password path clears without changing the .NET password.
        if (response.data.mustChangePassword) {
          localStorage.setItem(MUST_CHANGE_PASSWORD_KEY, "true");
          localStorage.setItem("accessToken", portal.accessToken);
          localStorage.setItem("userId", portal.user.userId);
          return navigate("/newPassword");
        }

        // Clear a stale flag left by an earlier abandoned forced change.
        localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);

        updateUser(portal.user);
        setUserFacilities(portal.facilities);
        setUserIncidentStatus(portal.incidentStatues);
        setUserIncidentTypes(portal.incidentTypes);
        updateAccessToken(portal.accessToken);
        setRoles(portal.roles);
        setMode(portal.mode);
        localStorage.setItem("accessToken", portal.accessToken);
        const currentTimestamp = moment().format("YYYY-MM-DD HH:mm:ss");
        localStorage.setItem("logInTimestamp", currentTimestamp);

        // ------ check if user was logged in before token expires -------
        const lastPath = params.get("lastPath");
        if (lastPath) {
          navigate(lastPath);
        } else {
          navigate("/user/dashboard");
        }

        if (localStorage.getItem("userId")) localStorage.removeItem("userId");
        toast.success("You have successfully logged in", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000,
          toastId: "1",
        });
        H.identify(portal.user.email, {
          id: portal.user.userId,
        });
      } catch (error: any) {
        console.log(error.response?.data);
        if (
          isInvalidCredentialsError(
            error.response?.status,
            error.response?.data?.error
          )
        ) {
          // Wrong password or unknown account: a centred popup with the reset guidance.
          setShowInvalidLogin(true);
        } else {
          // .NET returns { error }, the old Salesforce surface { message }.
          toast.error(
            error.response?.data?.error ||
              error.response?.data?.message ||
              "Login Failed",
            {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 2000,
              toastId: "1",
            }
          );
        }
        localStorage.removeItem("accessToken");
        localStorage.removeItem(AUTH_API_TOKEN_KEY);
      }
    },
  });

  return (
    <AuthLayout>
      <FormWrapper>
        <FormHeaderText>Log In</FormHeaderText>
        <FormSubTitle>Log in using your company access</FormSubTitle>
        <form
          name="loginForm"
          onSubmit={formik.handleSubmit}
          data-testid="login-form"
        >
          <FormInput
            label={"Email or Username"}
            name="emailOrUsername"
            data-testid="Email"
            value={formik.values.emailOrUsername}
            placeholder="Enter email or username"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={
              formik.touched.emailOrUsername && formik.errors.emailOrUsername
                ? formik.errors.emailOrUsername
                : null
            }
          />

          <FormInput
            data-testid="Password"
            label="Password"
            isImportant={true}
            name="password"
            placeholder="password"
            type="password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={
              formik.touched.password && formik.errors.password
                ? formik.errors.password
                : null
            }
          />

          <div className="mb-10 mt-4">
            {/* <input type="checkbox" className="border-red-200 mr-2" />
            <span className="text-sm font-medium">Remember me</span> */}
            <span className="float-right mt-1 text-sm font-medium">
              <Link to="/reset">
                <TextLight> Forgot password?</TextLight>
              </Link>
            </span>
          </div>

          <Button disabled={isLoading} type="submit">
            {" "}
            {isLoading ? "Authenticating..." : "Login"}{" "}
          </Button>

          <div className="mt-4 text-center text-sm font-medium">
            Don't have an account yet?{" "}
            <a
              href="https://mitigaterisk.com/contact-us/"
              target="_blank"
              rel="noreferrer"
              className="text-purple-300"
            >
              Contact us
            </a>
          </div>
        </form>
        {/* Inside FormWrapper: AuthLayout renders only its first child. */}
        <InvalidCredentialsModal
          open={showInvalidLogin}
          close={() => setShowInvalidLogin(false)}
        />
      </FormWrapper>
    </AuthLayout>
  );
};

export default Login;
